// StripeSettings — Office → Settings → Payments (Stripe).
//
// The classic app had this screen and the rebuild did not, so the only way to
// change the keys on the new app was to go back to the old one. Two endpoints,
// both already there:
//
//   GET  /api/invoices/stripe/settings   checkRole('admin')  → { configured, state,
//        key_source, has_encrypted_secret, publishable_key, publishable_key_masked,
//        secret_key_masked, webhook_configured, webhook_secret_masked,
//        is_live_mode, updated_at }
//   POST /api/invoices/stripe/settings   checkRole('admin')
//        { secret_key, publishable_key, webhook_secret?, is_live_mode? }
//
// Both are ADMIN ONLY at the route, which is why nothing here asks useCan:
// accounting holds `invoices: [create, read, update, delete, send, manage_all]`
// in database.js and is refused by every route in invoices.js
// (roleGateDivergence.test.js, sixteen entries). The Settings screen is already
// behind an admin check, and this panel re-states it rather than assuming it.
//
// ── The three things this screen must not get wrong ─────────────────────────
//
// 1. A REFUSED read is not "Stripe is not set up". The unconfigured state and
//    a 403/500/no-connection look identical if the failure is swallowed, and
//    the wrong one of those sends an admin to re-enter keys that are fine.
// 2. `state: 'undecryptable'` is not a fresh install either. It means the row
//    is there and the server cannot read it, i.e. the encryption key changed.
//    Saying "not set up" there hides the actual fault.
// 3. Saving REPLACES the row. server/routes/invoices.js runs
//    `DELETE FROM stripe_settings` and then one INSERT, with
//    `webhook_secret ? encrypt(webhook_secret) : null` — so a save that leaves
//    the webhook box empty wipes a webhook secret that was on file. That is
//    said on screen, next to the box, before it happens.
//
// ── …and the fourth, added 2026-09-14 after the owner read this screen ──────
//
// 4. A GREEN TICK IS A CLAIM. This screen showed:
//
//        ✓ Stripe is connected — the client can pay online.
//        MODE             Live — real payments
//        WEBHOOK SECRET   https:...hook
//        [ ] Live mode
//
//    Three separate things wrong with four lines:
//
//    · `https:...hook` is the webhook ENDPOINT URL in the signing-secret box.
//      Every real delivery from Stripe was being answered 400, so invoices
//      Stripe had collected were never marked paid — under a green tick saying
//      the client can pay online. `state: 'configured'` describes the KEYS and
//      nothing else; it is not a statement about the webhook and must not be
//      rendered as one. The headline is now downgraded by the webhook's state,
//      and the sentence comes from the server (`webhook_problem`).
//    · "MODE Live — real payments" was `stripe_settings.is_live_mode`, a stored
//      LABEL that nothing in the server reads. The KEY PREFIXES decide what
//      Stripe charges, so the mode is read from `key_mode` and the label is
//      mentioned only when the two disagree.
//    · The Live-mode box was unchecked under a mode that said Live, because it
//      is form state that started blank. Worse than confusing: saving from that
//      state wrote is_live_mode = 0 over a live install. It is seeded from the
//      server now, and `null` in the form means "whatever the server says".
//
// No money is computed here. There is no figure on this screen at all.

import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FwButton, FwField, FwInput, CheckRow, Notice } from '../../../ui/fw';
import { useRole } from '../../../hooks/usePermission';
import { get, post, describeError } from '../../shared/http';
import { Panel, FormError, FormRow, DetailList, DetailRow, useAction, fmtDateTime } from '../records/OfficePage';
import {
  MONEY_ENDPOINTS, mayUse, rejectStripeKeys, liveModeWarning, stripeState, STRIPE_STATES,
} from '../shared/moneyEndpoints';

// `is_live_mode: null` means "whatever the server has". It is not `false`,
// because `false` is an ANSWER — and answering it from an untouched form is how
// a key rotation used to write is_live_mode = 0 over a live installation.
const BLANK = { secret_key: '', publishable_key: '', webhook_secret: '', is_live_mode: null };

/** The webhook signing secret box, refused locally in the SERVER's words.
 *
 * server/utils/stripeKeyFormat.js `refuseWebhookSecret` is the authority and
 * answers 400; this exists so the message is immediate and identical rather
 * than a round trip away. moneyEndpointsContract.test.js lifts the sentence
 * out of the server file and requires this one to contain it character for
 * character, so any drift fails.
 *
 * Empty is NOT refused, in either place: the server reads an absent field as
 * "keep what is on file" (a key rotation) and '' as "clear it".
 */
const PASTED = {
  url: 'That looks like a URL. ',
  endpoint: 'That looks like a webhook endpoint ID. ',
  secret: 'That looks like a secret key. ',
  restricted: 'That looks like a restricted key. ',
  publishable: 'That looks like a publishable key. ',
};
// Byte-for-byte the server's WEBHOOK_WHERE (utils/stripeKeyFormat.js). It was
// NOT, until 2026-09-14: this copy carried a typographic apostrophe in
// "Stripe's" and the server an ASCII one, so an admin pasting a URL saw one
// sentence locally and a subtly different one from the 400. The contract test
// claimed to catch that and only compared the FIRST sentence — a guard
// narrower than the comment citing it. It now reads the whole sentence out of
// the server file, so a future drift of any character fails.
const WEBHOOK_RULE = 'The webhook signing secret starts with whsec_. '
  + 'It is on Stripe\'s webhook page, under "Signing secret", and it is shown once when the endpoint is created.';

export function webhookSecretRefusal(value) {
  const v = typeof value === 'string' ? value.trim() : '';
  if (!v || v.startsWith('whsec_')) return null;
  // The server's describePasted() (utils/stripeKeyFormat.js) in the SAME ORDER
  // and with the SAME patterns. Both matter:
  //
  //   order  — prefixes are tested before the URL pattern, so a future key
  //            prefix that happens to contain a colon can never fall through
  //            to "a URL". The server's comment says exactly that; this copy
  //            used to test the URL first.
  //   scheme — the server matches ANY `scheme://`, not just http(s). This copy
  //            matched only /^https?:/i, so `ftp://host/x` got "That looks
  //            like a URL. " from the 400 and a bare rule on screen. Review
  //            caught it on 2026-09-14, INSIDE the drift test added the same
  //            day: its inputs were all http and prefix values, so the one
  //            divergence left survived in the test's own blind spot.
  let clause = '';
  if (v.startsWith('pk_')) clause = PASTED.publishable;
  else if (v.startsWith('sk_')) clause = PASTED.secret;
  else if (v.startsWith('rk_')) clause = PASTED.restricted;
  else if (v.startsWith('we_')) clause = PASTED.endpoint;
  else if (/^https?:/i.test(v) || /^[a-z][a-z0-9+.-]*:\/\//i.test(v)) clause = PASTED.url;
  return `${clause}${WEBHOOK_RULE}`;
}

/**
 * Live or test, read off the KEYS.
 *
 * `is_live_mode` is a stored label; grep across server/ finds it only in the
 * GET that displays it and the POST that stores it, so it cannot make real
 * money move or stop it. `key_mode` is derived from the prefixes Stripe
 * actually bills against.
 */
const MODE_TEXT = {
  live: 'Live — the keys on file take real payments',
  test: 'Test — no real money moves',
  mixed: 'Mixed — one live key and one test key. Stripe will refuse the pair',
  unknown: 'Not stated by the keys — a restricted key (rk_) carries no mode in its name',
};

export function modeSentence(payload) {
  if (!payload || typeof payload !== 'object') return null;
  // An older server that does not send key_mode makes no claim about the keys,
  // so fall back to reporting the stored label AS a stored label.
  if (!Object.prototype.hasOwnProperty.call(MODE_TEXT, payload.key_mode)) {
    return payload.is_live_mode ? 'Live — real payments' : 'Test — no real money moves';
  }
  return MODE_TEXT[payload.key_mode];
}

/** Said only when the stored label contradicts the keys — which is what confused the owner. */
export function liveModeLabelNote(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const mode = payload.key_mode;
  if (mode !== 'live' && mode !== 'test') return null;
  const label = payload.is_live_mode ? 'live' : 'test';
  if (label === mode) return null;
  return `The saved "Live mode" box says ${label}, which disagrees with the keys. `
    + 'Nothing in the server reads that box — the keys decide what Stripe charges — but it is what this '
    + 'screen used to report as the mode.';
}

/** The webhook row, which must never read as healthy when it is not. */
export function webhookRowText(payload) {
  if (!payload || typeof payload !== 'object') return 'none on file';
  const masked = payload.webhook_secret_masked || 'on file';
  if (payload.webhook_status === 'signing_secret') return `${masked} — signature checking is on`;
  if (payload.webhook_status === 'malformed') return `${masked} — NOT a signing secret`;
  // A blob that will not decrypt is NOT "none on file". Saying so sends an
  // admin to paste a new secret when the fault is STRIPE_ENCRYPTION_KEY.
  // "are refused", not "are not checked": since the webhook fails closed on
  // an unreadable secret, nothing is taken on trust here — every delivery is
  // turned away and real payments are not being recorded (#694 review).
  if (payload.webhook_status === 'unreadable') return 'on file, but unreadable — deliveries are refused';
  if (payload.webhook_status === 'missing') return 'none on file — deliveries are not checked';
  return payload.webhook_configured ? masked : 'none on file';
}

/**
 * The title over the server's webhook sentence. One function for all three
 * problem states, because this is the line an admin reads first and it was
 * the one copy of the `unreadable` wording that got left behind: it said "not
 * being checked" above a body saying deliveries are REFUSED (#696 review).
 *
 * Tested per status by NAME (OfficeStripeSettings.test.js), with a render test
 * for `unreadable`. Nothing enumerates the server's WEBHOOK_* set, so a status
 * added there falls through to the `missing` title here — add its line and
 * its test together.
 */
export function webhookNoticeTitle(status) {
  if (status === 'malformed') return 'Stripe is being refused when it tells us an invoice was paid';
  if (status === 'unreadable') return 'Stripe is being refused — the signing secret on file cannot be read';
  return 'Payment notifications are not being checked';
}

/**
 * The headline, which is about the KEYS until the webhook contradicts it.
 *
 * STRIPE_STATES describes `state`, and `state` is computed from the keys alone
 * (routes/invoices.js: pk + sk → 'configured'). An unverified or rejected
 * webhook must not be rendered under "Stripe is connected" with a green tick,
 * so the TONE and the TITLE are replaced.
 *
 * The body is left exactly as STRIPE_STATES has it, and the server's sentence
 * is NOT appended: it is already rendered in full, once, in the webhook notice
 * directly below. Printing the same paragraph twice on the screen whose
 * complaint was "a bit confusing" would be its own answer to that complaint.
 */
export function headlineFor(copy, payload) {
  if (!copy) return null;
  const problem = payload && typeof payload.webhook_problem === 'string' ? payload.webhook_problem : null;
  if (!problem || !payload || payload.state !== 'configured') return copy;
  // `unreadable` is an OUTAGE now, the same as `malformed`: every delivery is
  // refused and payments go unrecorded. Only `missing` is the quieter
  // "accepted but unverified" case, and only it gets `warn`.
  const failing = payload.webhook_status === 'malformed' || payload.webhook_status === 'unreadable';
  return {
    tone: failing ? 'stop' : 'warn',
    title: failing
      ? 'Stripe is connected, but its payment notifications are failing'
      : 'Stripe is connected, but its payment notifications are not verified',
    body: copy.body,
  };
}

/** What the server says the encryption key is read from, as a sentence. */
export function keySourceNote(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (!('key_source' in payload)) return null;
  const src = payload.key_source;
  if (!src) {
    return 'No encryption key is loaded on the server (neither STRIPE_ENCRYPTION_KEY nor ENCRYPTION_KEY is set). '
      + 'Keys saved now cannot be read back, so saving will be refused.';
  }
  return `The server encrypts these with ${src}.`;
}

export function StripeSection() {
  const role = useRole();
  const allowed = mayUse('stripeSettingsRead', role);
  const qc = useQueryClient();
  const [form, setForm] = useState(BLANK);
  const [saved, setSaved] = useState(null);
  const [refused, setRefused] = useState(null);
  const { busy, error, run } = useAction();

  const q = useQuery({
    queryKey: ['office', 'stripe-settings'],
    queryFn: () => get(MONEY_ENDPOINTS.stripeSettingsRead.path),
    enabled: allowed,
    staleTime: 30000,
  });

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
    setSaved(null);
    setRefused(null);
  };

  const state = stripeState(q.data);
  const copy = state ? STRIPE_STATES[state] : null;
  const headline = useMemo(() => headlineFor(copy, q.data), [copy, q.data]);
  // The Live-mode box follows the server until somebody touches it. `null` is
  // "not touched"; a save sends the resolved value, so an untouched form can no
  // longer flip a live installation to test.
  const liveMode = form.is_live_mode === null ? !!(q.data && q.data.is_live_mode) : form.is_live_mode;
  const localRefusal = useMemo(
    () => rejectStripeKeys(form) || webhookSecretRefusal(form.webhook_secret),
    [form],
  );
  const modeWarning = useMemo(
    () => liveModeWarning({ ...form, is_live_mode: liveMode }),
    [form, liveMode],
  );
  const typed = !!(form.secret_key || form.publishable_key || form.webhook_secret);
  // Only meaningful once the read answered: "there is a webhook on file" is a
  // claim about the server, and a failed read has not made it.
  //
  // A MALFORMED value is excluded: "paste it again to keep it" is the wrong
  // advice about a value that is refusing every delivery, and the headline
  // above is already telling them to replace it.
  const wipesWebhook = !!(q.data && q.data.webhook_configured)
    && q.data.webhook_status !== 'malformed'
    && !form.webhook_secret;

  const save = async () => {
    setSaved(null);
    setRefused(null);
    if (localRefusal) { setRefused(localRefusal); return; }
    const ok = await run(async () => {
      const r = await post(MONEY_ENDPOINTS.stripeSettingsSave.path, {
        secret_key: form.secret_key.trim(),
        publishable_key: form.publishable_key.trim(),
        webhook_secret: form.webhook_secret.trim() || undefined,
        is_live_mode: liveMode,
      });
      setSaved(r && r.message ? r.message : 'Stripe settings saved.');
      await qc.invalidateQueries({ queryKey: ['office', 'stripe-settings'] });
    }, 'Could not save the Stripe keys.');
    if (ok) setForm(BLANK);
  };

  if (!allowed) {
    return (
      <Panel title="Payments (Stripe)">
        <Notice tone="info" data-stripe="refused">
          Only an administrator can see or change the Stripe keys — every route under
          /api/invoices/stripe is admin-only.
        </Notice>
      </Panel>
    );
  }

  return (
    <Panel title="Payments (Stripe)">
      {/* The read state comes FIRST and is never conflated with a failure.
          `q.isPending` is a third thing again: we have not asked yet. */}
      {q.isPending ? <p className="or-muted" data-stripe="loading">Checking what is configured…</p> : null}
      {q.isError ? (
        <Notice tone="stop" data-stripe="load-failed">
          Could not read the Stripe settings — {describeError(q.error)}. This does <strong>not</strong> mean
          Stripe is unconfigured; nothing is known either way until this loads.
          {' '}<FwButton variant="quiet" onClick={() => q.refetch()}>Try again</FwButton>
        </Notice>
      ) : null}

      {q.data && headline ? (
        <Notice tone={headline.tone} title={headline.title} data-stripe-state={state}>
          {headline.body}
        </Notice>
      ) : null}
      {q.data && !copy ? (
        <Notice tone="warn" data-stripe-state="unknown">
          The server answered with a state this screen does not recognise
          ({String(q.data.state)}). Nothing has been changed.
        </Notice>
      ) : null}

      {q.data && copy ? (
        <div data-stripe="current">
          <DetailList>
            <DetailRow label="Mode">{modeSentence(q.data)}</DetailRow>
            <DetailRow label="Publishable key">{q.data.publishable_key_masked || 'none on file'}</DetailRow>
            <DetailRow label="Secret key">{q.data.secret_key_masked || (q.data.has_encrypted_secret ? 'on file, cannot be decrypted' : 'none on file')}</DetailRow>
            <DetailRow label="Webhook secret">{webhookRowText(q.data)}</DetailRow>
            {/* Through fmtDateTime, not raw. `stripe_settings.updated_at` is a
                SQLite DATETIME and arrives as "2026-09-10 14:23:05" — a UTC
                wall-clock string with no zone marker. Printed bare it is both
                ugly and WRONG by four or five hours for a Connecticut admin
                reading "when did these keys last change", on the screen where
                that question is usually being asked because something broke.
                Every other datetime in the admin screens already goes through
                this helper. */}
            {q.data.updated_at ? <DetailRow label="Last saved">{fmtDateTime(q.data.updated_at)}</DetailRow> : null}
          </DetailList>
        </div>
      ) : null}
      {q.data && liveModeLabelNote(q.data) ? (
        <p className="or-muted" data-stripe="mode-label">{liveModeLabelNote(q.data)}</p>
      ) : null}
      {q.data && keySourceNote(q.data) ? (
        <p className="or-muted" data-stripe="key-source">{keySourceNote(q.data)}</p>
      ) : null}

      {/* The webhook, said in full and separately from the keys. The sentence
          is the SERVER's (`webhook_problem`) — this screen reports it rather
          than composing a second version of the same claim that could drift.
          There is deliberately no "verified" notice for the healthy case: a
          signing secret that no longer matches Stripe's is well-formed and
          fails exactly like a URL, and only a real delivery knows. */}
      {q.data && q.data.webhook_problem ? (
        <Notice
          tone={q.data.webhook_status === 'malformed' || q.data.webhook_status === 'unreadable' ? 'stop' : 'warn'}
          title={webhookNoticeTitle(q.data.webhook_status)}
          data-stripe="webhook-problem"
          data-webhook-status={q.data.webhook_status}
        >
          {q.data.webhook_problem}
        </Notice>
      ) : null}

      <FormError error={error} />
      {refused ? <Notice tone="stop" data-stripe="refused-locally">{refused}</Notice> : null}
      {saved ? <Notice tone="ok" data-stripe="saved">{saved}</Notice> : null}

      <FormRow>
        <FwField
          label="Publishable key"
          htmlFor="or-stripe-pk"
          hint="Starts pk_test_ or pk_live_. Safe to paste — it is the key the client's browser uses."
        >
          <FwInput value={form.publishable_key} onChange={set('publishable_key')} autoComplete="off" spellCheck="false" data-field="publishable_key" />
        </FwField>
        <FwField
          label="Secret key"
          htmlFor="or-stripe-sk"
          hint="Starts sk_ or rk_. Stored encrypted; it is never sent back to this screen."
        >
          <FwInput type="password" value={form.secret_key} onChange={set('secret_key')} autoComplete="new-password" spellCheck="false" data-field="secret_key" />
        </FwField>
      </FormRow>
      <FwField
        label="Webhook signing secret (optional)"
        htmlFor="or-stripe-wh"
        hint="Starts whsec_ — the Signing secret on Stripe's webhook page, NOT the endpoint URL and not the we_ endpoint ID. Without it the webhook that marks invoices paid is unverified; with the wrong one, every notification is refused."
      >
        <FwInput type="password" value={form.webhook_secret} onChange={set('webhook_secret')} autoComplete="new-password" spellCheck="false" data-field="webhook_secret" />
      </FwField>
      <CheckRow
        data-field="is_live_mode"
        label="Live mode"
        meta="A label only — the key prefixes are what decide whether real money moves. Starts where the server has it."
        checked={liveMode}
        onToggle={(next) => { setForm((f) => ({ ...f, is_live_mode: next })); setSaved(null); setRefused(null); }}
      />

      {modeWarning ? <Notice tone="warn" data-stripe="mode-warning">{modeWarning}</Notice> : null}
      {wipesWebhook ? (
        <Notice tone="warn" data-stripe="wipes-webhook">
          A webhook secret is on file. Saving replaces the whole row, so leaving the webhook box empty
          clears it and Stripe&apos;s payment notifications stop being verified. Paste it again to keep it.
        </Notice>
      ) : null}
      <Notice tone="info" data-stripe="replace-warning">
        Saving replaces both keys. There is no way to change one on its own.
      </Notice>

      <div className="or-actions">
        <FwButton icon="dollar" onClick={save} loading={busy} disabled={!typed || !!localRefusal}>Save Stripe keys</FwButton>
        {typed && localRefusal ? <span className="or-muted" data-stripe="local-hint">{localRefusal}</span> : null}
      </div>
    </Panel>
  );
}

export default StripeSection;
